/**
 * useResolveScenarioForSource.ts
 * FEAT-2026-0007 Phase 6D.4 — Shared scenario resolve logic.
 * Updated BUG-2026-0118 — empty resumed drafts regenerate their tasks.
 *
 * Extracted from ScenarioSourcePicker so the same create-or-load-draft +
 * generate flow can be called from BOTH:
 *   - ScenarioSourcePicker (manual selection)
 *   - ScenarioPlanningPage deep-link path (?source=&sourceKey= URL params)
 *
 * Usage:
 *   const result = await resolveScenarioForSource('Quote', quoteKey);
 *
 * Returns a ResolveResult containing:
 *   - scenario: the Scenario that was created or resumed
 *   - message: human-readable status string for a snackbar
 *   - severity: 'success' | 'info' (never error — errors are thrown)
 *
 * Throws on hard failures (network error, 4xx other than "no active draft").
 * The caller is responsible for catching and displaying errors.
 *
 * BUG-2026-0118: the resume path no longer assumes the draft still holds
 * tasks. When every ScheduleTask of the resumed draft has been (soft-)deleted
 * since it was created, the source's tasks are regenerated under the same
 * ScenarioKey so the user never lands on a silently empty plan.
 *
 * This module is a thin binding of the real services onto the pure decision
 * core in resolveScenarioCore.ts (kept service-import-free so it can be
 * unit-tested under tsx — see fix-BUG-2026-0118.ts). All behaviour lives in
 * resolveScenarioWithDeps; keep this file logic-free.
 */

import {
  getActiveScenarioForSource,
  createScenarioDraft,
  listScenariosForSource,
} from '../../../../services/scheduleScenario';
import { generateScheduleTasks } from '../../../../services/scheduleTaskGeneration';
import { listTasksForSource } from '../../../../services/scheduleTasks';
import {
  resolveScenarioWithDeps,
  type ResolveResult,
  type ResolveLocked,
  type ResolveOutcome,
  type ResolveScenarioOpts,
  type ResolveScenarioDeps,
} from './resolveScenarioCore';
import type { ScenarioSourceType } from '../../../../services/scheduleScenario';

// Re-export so existing importers of the result types keep working.
export type { ResolveResult, ResolveLocked, ResolveOutcome, ResolveScenarioOpts };

// ---------------------------------------------------------------------------
// Production dependency bindings
// ---------------------------------------------------------------------------

const productionDeps: ResolveScenarioDeps = {
  getActiveScenarioForSource,
  createScenarioDraft,
  generateScheduleTasks,
  listTasksForSource,
  listScenariosForSource,
};

// ---------------------------------------------------------------------------
// Shared resolve function
//
// This is a plain async function (not a hook) so it can be imported and called
// from both a React component and a custom hook without violating hook rules.
// Callers that want to memoize it can wrap it in useCallback.
// ---------------------------------------------------------------------------

/**
 * Resolves (or creates) a scenario for the given source.
 *
 * Sequence (full details in resolveScenarioCore.ts):
 *   1. getActiveScenarioForSource(sourceType, sourceKey)
 *      - found, with ≥1 live task → return it (resume path)
 *      - found, with 0 live tasks → regenerate tasks under the same
 *        ScenarioKey, then return it (BUG-2026-0118)
 *   2. If not found (2026-07-10 silent-duplicate fix): probe
 *      listScenariosForSource — when a LOCKED (Submitted) plan exists,
 *      return { kind: 'locked', scenario } so the caller can offer
 *      Open / Reopen to Edit / Create new draft explicitly. Skipped with
 *      opts.forceCreate (the user chose "Create new draft").
 *   3. If still nothing:
 *      a. createScenarioDraft({ SourceType, SourceKey })
 *      b. generateScheduleTasks({ source, sourceKey, scenarioKey })
 *         (soft failure: generate error is reported in message but scenario
 *         still returned)
 *      c. return the new scenario
 *
 * Throws on hard failures (createScenarioDraft network error, locked-plan
 * probe failure, etc.). Callers MUST branch on `result.kind`.
 */
export async function resolveScenarioForSource(
  sourceType: ScenarioSourceType,
  sourceKey: string,
  opts?: ResolveScenarioOpts,
): Promise<ResolveOutcome> {
  return resolveScenarioWithDeps(sourceType, sourceKey, productionDeps, opts);
}
